// src/modules/app-views/fiscal-config/pages/Document/tabs/NfceTab/NfceCard.jsx
import {
  Card,
  Row,
  Col,
  Space,
  Switch,
  Tooltip,
  Spin,
  Alert,
} from 'antd';
import {
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleInputWithButton from 'components/SimpleComponents/SimpleInputWithButton';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { ENVIRONMENT_OPTIONS, FISCAL_PROVIDER_OPTIONS } from '../CertificateTab/constants';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NfceCard = ({
  nfceEnabled,
  locked,
  validating,
  validationError,
  cscLinkUrl,
  cscLinkName,
  onToggle,
  onOpenCscTokenModal,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Fields are disabled when service is enabled (user must disable to edit)
  const fieldsDisabled = nfceEnabled || locked;

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Spin spinning={validating} tip={translateX('validating_service')}>
      <Card
        title={<div className="text-muted">{translateX('nfce_configuration')}</div>}
        extra={
          <Space>
            {nfceEnabled && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            <span>{nfceEnabled ? translateX('activated') : translateX('deactivated')}</span>
            <Tooltip
              title={
                nfceEnabled ? translateX('deactivate_to_edit') : translateX('activate_nfce_feature')
              }
            >
              <Switch checked={nfceEnabled} onChange={onToggle} disabled={locked || validating} />
            </Tooltip>
          </Space>
        }
      >
        {/* Validation Error Alert */}
        {validationError && (
          <Alert
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            title={translateX('validation_failed')}
            description={validationError}
            style={{ marginBottom: 16 }}
            closable
          />
        )}

        {/* Row 1: Provider and Environment */}
        <Row gutter={ROW_GUTTER}>
          {/* Fiscal Provider */}
          <Col xs={24} sm={12} lg={8}>
            <SimpleSelect
              name="fiscalData.nfce.provider"
              label="sefaz_connection"
              options={FISCAL_PROVIDER_OPTIONS}
              disabled={fieldsDisabled}
              placeholder="sefaz_connection_channel"
            />
          </Col>

          {/* Environment */}
          <Col xs={24} sm={12} lg={8}>
            <SimpleSelect
              name="fiscalData.nfce.environment"
              label="environment"
              options={ENVIRONMENT_OPTIONS}
              disabled={fieldsDisabled}
            />
          </Col>

          {/* Serie */}
          <Col xs={24} sm={12} lg={8}>
            <SimpleNumber
              name="fiscalData.nfce.currentSerie"
              label="serie"
              min={1}
              max={999}
              disabled={fieldsDisabled}
            />
          </Col>
        </Row>

        {/* Row 2: CSC Configuration */}
        <Row gutter={ROW_GUTTER} style={{ marginTop: 16 }}>
          {/* CSC ID */}
          <Col xs={24} sm={6} lg={4}>
            <SimpleNumber
              name="fiscalData.nfce.csc.id"
              label="csc_id"
              min={1}
              max={999}
              disabled={fieldsDisabled}
            />
          </Col>

          {/* CSC Token - Write-only with modal */}
          <Col xs={24} sm={18} lg={12}>
            <SimpleInputWithButton
              name="fiscalData.nfce.csc.tokenMasked"
              label="csc_token"
              placeholder="not_configured"
              readOnly
              disabled={fieldsDisabled}
              buttonIcon={<KeyOutlined />}
              buttonType="default"
              buttonTooltip="configure_csc_token"
              buttonDisabled={fieldsDisabled}
              onButtonClick={onOpenCscTokenModal}
              labelLink={cscLinkUrl}
              labelLinkTooltip={cscLinkName ? `Sefaz ${cscLinkName}` : null}
            />
          </Col>

          {/* Last Number - For migration purposes */}
          <Col xs={24} sm={12} lg={8}>
            <SimpleNumber
              name="fiscalData.nfce.lastNumber"
              label="last_number"
              min={0}
              disabled={fieldsDisabled}
              tooltip="last_number_tooltip"
            />
          </Col>
        </Row>

        {/* Info message when enabled */}
        {nfceEnabled && (
          <Alert
            type="info"
            showIcon
            message={translateX('service_active')}
            description={translateX('deactivate_to_edit_settings')}
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </Spin>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default NfceCard;
